/**
 * ESR Mapovis plugin integration.
 *
 * The plugin's script (window.esrMapovis) is theme-independent and sets itself
 * up on the first page. This module ties it to the theme: it sets up / tears
 * down the components of each page Barba swaps (MODULES init / destroy), routes
 * the plugin's scroll requests through Lenis so they land below the fixed
 * header, and lets the plugin's dialogs (package details, enquiry) scroll
 * natively (data-lenis-prevent) while Lenis is paused.
 */
import { getHeaderOffset } from '../core/scroll-manager.js';

let lenisInstance = null;

/**
 * Scrolls a requested element into the visible area below the header, centred
 * when it fits.
 *
 * @param {CustomEvent} event esr-mapovis:scroll-to ({ element }).
 * @return {void}
 */
function onScrollTo(event) {
    const element = event.detail?.element;
    if (!lenisInstance || !element) return;

    event.preventDefault();
    const rect = element.getBoundingClientRect();
    const targetY = rect.top + window.scrollY;
    const headerOffset = getHeaderOffset(targetY);
    const visible = window.innerHeight + headerOffset;
    const centre = Math.max(0, (visible - rect.height) / 2);

    lenisInstance.scrollTo(element, { offset: headerOffset - centre });
}

/** Pauses page scrolling while a package modal is open. */
function onModalOpen() {
    lenisInstance?.stop();
}

/** Resumes page scrolling when the modal closes. */
function onModalClose() {
    lenisInstance?.start();
}

/**
 * Sets up the Mapovis components in a container.
 *
 * @param {Object} ctx Module context ({ container, lenis }).
 * @return {void}
 */
export function initializeEsrMapovis({ container, lenis }) {
    window.esrMapovis?.init(container || document);
    (container || document).querySelectorAll('dialog.esr-mapovis').forEach((dialog) => {
        dialog.setAttribute('data-lenis-prevent', '');
    });

    lenisInstance = lenis || null;
    document.removeEventListener('esr-mapovis:scroll-to', onScrollTo);
    document.addEventListener('esr-mapovis:scroll-to', onScrollTo);
    document.removeEventListener('esr-mapovis:modal-open', onModalOpen);
    document.addEventListener('esr-mapovis:modal-open', onModalOpen);
    document.removeEventListener('esr-mapovis:modal-close', onModalClose);
    document.addEventListener('esr-mapovis:modal-close', onModalClose);
}

/**
 * Tears down the Mapovis components in a container.
 *
 * @param {Object} ctx Module context ({ container }).
 * @return {void}
 */
export function destroyEsrMapovis({ container }) {
    window.esrMapovis?.destroy(container || document);
    document.removeEventListener('esr-mapovis:scroll-to', onScrollTo);
    document.removeEventListener('esr-mapovis:modal-open', onModalOpen);
    document.removeEventListener('esr-mapovis:modal-close', onModalClose);
    lenisInstance?.start();
}
